import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Hidden from "@insite/mobius/Hidden/Hidden";
import { IconWrapper } from "@insite/mobius/Icon";
import { Link } from "@insite/mobius/index";
import MobiusLink, { LinkPresentationProps } from "@insite/mobius/Link";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import MailIcon from "@tsesco/Icons/MailIcon";
import TelephoneIcon from "@tsesco/Icons/TelephoneIcon";
import * as cssLinter from "css";
import * as React from "react";
import { FC } from "react";

import { css } from "styled-components";

const enum fields {
    destination = "destination",
    overrideTitle = "overrideTitle",
    customCSS = "customCSS",
    para = "para",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.destination]: LinkFieldValue;
        [fields.overrideTitle]: string;
        [fields.customCSS]: string;
        [fields.para]: string;
    };
}

const linkStyles: LinkPresentationProps = {
    typographyProps: {
        variant: "headerSecondary",
        transform: "capitalize",
    },
    icon: {
        iconProps: {
            size: 22,
            color: "secondary",
        },
    },
    color: "secondary",
    css: css`
        padding: 0px;
        ${IconWrapper} {
            > svg {
                > path {
                    ${({ theme }) => {
                        return breakpointMediaQueries(
                            theme,
                            [
                                null,
                                css`
                                    fill: #fff;
                                    stroke: #fff;
                                `,
                                null,
                                null,
                                null,
                            ],
                            "max",
                        );
                    }}
                }
            }
        }
        &:hover {
            ${TypographyStyle} {
                text-decoration: underline;
            }
        }
    `,
};

export const SEHeaderContactUs: FC<OwnProps> = ({ fields }) => {
    const { title, url } = useGetLink(fields.destination);
    const styles = useMergeStyles("link", linkStyles);
    const [isFlyoutOpen, setIsFlyoutOpen] = React.useState(false);
    const componentRef = React.useRef<HTMLDivElement>(null);
    const history = useHistory();

    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (componentRef.current && !componentRef.current.contains(event.target as Node)) {
                setIsFlyoutOpen(false);
            }
        };

        document.addEventListener("click", handleClickOutside);

        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, []);

    if (!url) {
        return null;
    }

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
            align-items: center;
            > div {
                > div {
                    > div {
                        display: flex;
                    }
                }
            }
            &:hover + ${StyledWrapper} ${StyledWrapper} {
                display: block;
            }
        `,
    };

    const contactWrapper = {
        css: css`
            display: block;
            display: flex;
            align-items: center;
            font-size: smaller;
            text-decoration: none !important;
            transition: color 0.3s ease;
            ${TypographyStyle} {
                display: flex;
                align-items: center;
                text-decoration: none !important;
                svg {
                    margin-right: 5px;
                    width: 14px;
                    height: 14px;
                }
                &:hover {
                    color: ${getColor("primary")} !important;
                    svg path {
                        fill: ${getColor("primary")} !important;
                    }
                }
            }
        `,
    };

    const emailWrapper = {
        css: css`
            display: block;
            display: flex;
            align-items: center;
            font-size: smaller;
            margin-top: 5px;
            text-decoration: none !important;
            transition: color 0.3s ease;
            ${TypographyStyle} {
                display: flex;
                align-items: center;
                text-decoration: none !important;
                svg {
                    margin-right: 5px;
                    width: 14px;
                    height: 14px;
                }
                &:hover {
                    color: ${getColor("primary")} !important;
                    svg path {
                        color: ${getColor("primary")} !important;
                    }
                }
            }
        `,
    };

    const flyoutContent = {
        css: css`
            display: ${isFlyoutOpen ? "block" : "none"};
            visibility: visible;
            opacity: 1;
            transform: translateY(10px) translateX(-50px);
            transition-delay: 0s, 0s, 0.2s;
            position: absolute;
            left: auto;
            z-index: 99;
            transition: all 0.5s ease-in-out 0s, visibility 0s linear 0s, z-index 0s linear 0.02s;
            box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.1);
            border: 1px solid #dfe3e6;
            background: white;
            padding: 10px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    css`
                        padding: 10px 5px;
                        margin-top: 12px;
                    `,
                    null,
                    null,
                ]);
            }}
        `,
    };

    const handleFlyoutToggle = (e: any) => {
        e.preventDefault();
        const phoneNumber = "1-800-318-4618";
        const isMobile = window.innerWidth <= 575;
        const ispad = window.innerWidth > 575;
        if (isMobile) {
            window.location.href = `tel:${phoneNumber}`;
        }
        setIsFlyoutOpen(!isFlyoutOpen);

        if (ispad) {
            history.push("/contact-us");
        }
    };

    return (
        <StyledWrapper ref={componentRef} {...customCssWrapper}>
            <MobiusLink
                className="link"
                {...styles}
                href={url}
                icon={{ ...styles?.icon, iconProps: { src: TelephoneIcon, ...styles?.icon?.iconProps } }}
                onClick={handleFlyoutToggle}
            >
                <StyledWrapper {...customCssWrapper}>
                    <StyledWrapper>
                        <Hidden below="lg">
                            <TypographyStyle>{fields.overrideTitle || title || url}</TypographyStyle>
                        </Hidden>
                        {/* <Hidden below="md"> */}
                        <StyledWrapper {...flyoutContent}>
                            <TypographyStyle {...contactWrapper}>
                                <Link href="tel:1-800-318-4618">
                                    <TelephoneIcon />
                                    call: 1-800-318-4618
                                </Link>
                            </TypographyStyle>
                            <TypographyStyle {...emailWrapper}>
                                <Link href="/contact-us">
                                    <MailIcon />
                                    Email Us
                                </Link>
                            </TypographyStyle>
                        </StyledWrapper>
                        {/* </Hidden> */}
                    </StyledWrapper>
                </StyledWrapper>
            </MobiusLink>
        </StyledWrapper>
    );
};

const defaultCustomCss = `.link {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: SEHeaderContactUs,
    definition: {
        group: "Common",
        displayName: "Header Contact Us",
        icon: "link",
        fieldDefinitions: [
            {
                name: fields.destination,
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "General",
                isRequired: true,
                tab: basicTab,
            },
            {
                name: fields.overrideTitle,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
